import { createAsyncThunk } from '@reduxjs/toolkit';
import { User } from 'oidc-client';
import LOGIN_FLOW_ACTIONS from './login-action-types';
import {
    authenticateUser,
    getUserInfo,
    initiateLogin,
    initiateLogout,
    isPOCRedirectValidAPI,
    processLoginCallback,
} from './login-api';
import {
    authAPIFailed,
    updateLoginStatus,
    updateOriginalRedirectUrl,
} from './login-slice';
import ROUTES from '../../constants/routes';

export interface IAuth0UserInfo extends User {
    isUserAuthenticated: boolean | undefined;
    identityAccessToken: string | undefined;
    accessToken: string | undefined;
    identityAccessTokenExpiration: number | undefined;
    firstName: string;
    lastName: string;
    id: number;
    originalRedirectUrl: string;
    role: string | undefined;
    email: string | undefined;
}

export const loginFlow = createAsyncThunk(
    LOGIN_FLOW_ACTIONS.LOGIN_FLOW,
    async (_, thunkAPI) => {
        try {
            const oAuthUserInfo = (await getUserInfo()) as IAuth0UserInfo;
            await loginAPI(oAuthUserInfo, thunkAPI);
        } catch (error) {
            console.error('Error retrieving user information:', error);
            await initiateLogin();
        }
    }
);

export const completeLoginFlow = createAsyncThunk(
    'login/completeLoginFlowStatus',
    async (_, thunkAPI) => {
        try {
            const oAuthUserInfo =
                (await processLoginCallback()) as IAuth0UserInfo;
            await loginAPI(oAuthUserInfo, thunkAPI);
        } catch (error) {
            thunkAPI.dispatch(authAPIFailed(error));
        }
    }
);

export const logoutFlow = createAsyncThunk(
    'logout/logoutFlowStatus',
    async (_, thunkAPI) => {
        try {
            await initiateLogout();
        } catch (error) {
            console.error('Error during logout:', error);
            thunkAPI.dispatch(authAPIFailed(error));
        }
    }
);

async function loginAPI(oAuthUserInfo: IAuth0UserInfo, thunkAPI: any) {
    const {
        isUserAuthenticated,
        identityAccessToken,
        accessToken,
        identityAccessTokenExpiration,
        firstName,
        lastName,
        originalRedirectUrl,
    } = oAuthUserInfo;
    try {
        const { data } = await authenticateUser(
            identityAccessToken,
            identityAccessTokenExpiration as number,
            accessToken
        );

        thunkAPI.dispatch(
            updateLoginStatus({
                ...data,
                isUserAuthenticated,
                originalRedirectUrl,
            })
        );

        // if we don't have a redirect url, send to home page
        window.location.href = data.redirectUrl || ROUTES.WELCOME;
    } catch (err: any) {
        // eslint-disable-next-line @typescript-eslint/no-throw-literal
        throw {
            error: err.response,
            firstName,
            lastName,
            isUserAuthenticated: false,
        };
    }
}

export const isPOCRedirectValid = createAsyncThunk(
    'login/isPOCRedirectValid',
    async ({ ecrId }: { ecrId: string }, thunkAPI) => {
        try {
            const { data } = await isPOCRedirectValidAPI(ecrId);
            thunkAPI.dispatch(updateOriginalRedirectUrl(data.redirectUrl));
        } catch (error) {
            console.error('Error during check isPOCRedirectValid:', error);
            thunkAPI.dispatch(
                authAPIFailed({ error, isUserAuthenticated: false })
            );
        }
    }
);
